import React, { useMemo } from 'react'
import PropTypes from 'prop-types'
import { Link } from 'react-router-dom'
import { Alert } from 'goustouicomponents/dist/Alert'
import { Heading } from 'goustouicomponents/dist/Heading'
import {
  BorderStyle,
  Box,
  Color,
  FontWeight,
  Icon,
  IconVariant,
  Overflow,
  Paragraph,
  Space,
  Grid,
  Col,
  Display,
  AlignItems,
  Heading2,
} from '@gousto-internal/citrus-react'
import DOMPurify from 'dompurify'
import { ContentLoader } from '../ContentLoader'
import { client } from '../../config/routes'
import { createSlugFromUrl, removeHtmlTags } from '../../utils'
import { useFetchSectionArticles } from '../../hooks/useFetchSectionArticles'
import css from './ArticleRelated.module.css'

const ArticleRelated = ({ articleID, sectionID }) => {
  const {
    isSectionArticlesFetching,
    sectionArticlesResponse,
  } = useFetchSectionArticles(sectionID)
  const reducedArticles = useMemo(() => {
    if (!sectionArticlesResponse?.articles) {
      return []
    } else {
      return sectionArticlesResponse?.articles
        .filter((article) => article.id.toString() !== articleID)
        .map((article) => {
          const articleNameSlug = createSlugFromUrl(article.html_url)

          return ({
            id: article.id,
            title: article.title,
            url: `${client.article}/${articleNameSlug}/${article.id}`,
            body: DOMPurify.sanitize(removeHtmlTags(article.body)),
          })
        })
    }
  }, [sectionArticlesResponse, articleID])

  if (reducedArticles.length) {
    return (
      <>
        <Heading2 size={5}>
          Articles in this section
        </Heading2>
        <Space size={8} />
        <Grid gap={6}>
          {reducedArticles.map((article) => (
            <Col key={article.id} size={[12, 6]}>
              <Link
                key={article.id}
                to={article.url}
                data-tracking-action="kb_click_articles_in_this_section"
                data-tracking-property={JSON.stringify({
                  source_article_id: articleID,
                  new_article_id: `${article.id}`,
                })}
                className={css.articleLink}
              >
                <Box
                  display="flex"
                  borderStyle={BorderStyle.Solid}
                  borderWidth={0.5}
                  borderColor={Color.ColdGrey_100}
                  height={132}
                  borderRadius={2}
                  paddingH={5}
                  paddingV={4}
                  position="relative"
                  className={css.articleBoxGradient}
                >
                  <Box
                    overflowY={Overflow.Hidden}
                    paddingRight={5}
                  >
                    <Paragraph
                      fontWeight={FontWeight.SemiBold}
                      color={Color.ColdGrey_800}
                      size={2}
                    >
                      {article.title}
                    </Paragraph>
                    <Space size={1} />
                    <Paragraph color={Color.NeutralGrey_600}>
                      {article.body}
                    </Paragraph>
                  </Box>
                  <Box
                    display={Display.Flex}
                    alignItems={AlignItems.Center}
                  >
                    <Icon name="chevron_right" variant={IconVariant.Actionable} />
                  </Box>
                </Box>
              </Link>
            </Col>
          ))}
        </Grid>
      </>
    )
  }

  if (isSectionArticlesFetching) {
    return <ContentLoader />
  }

  return (
    <Alert type="danger">
      <Heading type="h2" size="fontStyleM">Something went wrong!</Heading>
      <p>There is an issue fetching the articles in this section.</p>
    </Alert>
  )
}

ArticleRelated.propTypes = {
  articleID: PropTypes.string.isRequired,
  sectionID: PropTypes.number.isRequired,
}

export { ArticleRelated }
